import React, { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { FiShoppingBag, FiSearch, FiHeart, FiStar, FiFilter, FiTag } from 'react-icons/fi';
import ProductModal from '../../components/ProductModal/ProductModal';
import './Store.css';

const Store = () => {
  const navigate = useNavigate();
  const { productSlug } = useParams();
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(true);
  const [selectedProduct, setSelectedProduct] = useState(null);

  const slugify = useCallback((text) => {
    if (!text) return '';
    return text
      .toString()
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/(^-|-$)/g, '');
  }, []);

  const getProductSlug = useCallback(
    (product) => {
      if (product?.slug) return product.slug;
      const base = slugify(product?.name) || 'producto';
      return product?.id ? `${base}-${product.id}` : base;
    },
    [slugify]
  );

  useEffect(() => {
    loadStore();
  }, []);

  useEffect(() => {
    if (!productSlug) {
      setSelectedProduct(null);
      return;
    }
    if (!products || products.length === 0) return;

    const match = products.find(p => getProductSlug(p) === productSlug);
    setSelectedProduct(match || null);
  }, [productSlug, products, getProductSlug]);

  const loadStore = async () => {
    try {
      const res = await fetch('http://localhost:5000/api/store');
      const data = await res.json();
      setProducts(data.products || []);
      setCategories(data.categories || []);
    } catch (error) {
      console.error('Error loading store:', error);
      setProducts([]);
      setCategories([]);
    } finally {
      setLoading(false);
    }
  };

  const filteredProducts = products.filter(product => {
    const matchesCategory = selectedCategory === 'all' || 
                           product.category_slug === selectedCategory ||
                           product.category === selectedCategory;
    const matchesSearch = product.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         (product.description && product.description.toLowerCase().includes(searchQuery.toLowerCase()));
    return matchesCategory && matchesSearch;
  });

  const featuredProducts = products.filter(p => p.is_featured || p.featured);

  const openProductModal = (product, e) => {
    if (e?.preventDefault) e.preventDefault();
    const slug = getProductSlug(product);
    navigate(`/store/${slug}`);
  };

  const closeProductModal = () => {
    navigate('/store');
  };

  const getProductImage = (product) => {
    if (product.images && product.images.length > 0) {
      return product.images[0];
    }
    return product.image || 'https://via.placeholder.com/400';
  };

  const getDiscountPercent = (product) => {
    if (product.discount_percent) return product.discount_percent;
    if (product.discount) return product.discount;
    if (product.compare_price && product.price) {
      return Math.round((1 - product.price / product.compare_price) * 100);
    }
    return 0;
  };

  const maxDiscountPercent = products.reduce((max, p) => {
    const d = Number(getDiscountPercent(p)) || 0;
    return d > max ? d : max;
  }, 0);

  if (loading) {
    return (
      <div className="store-loading">
        <FiShoppingBag className="loading-icon" />
        <p>Cargando tienda...</p>
      </div>
    );
  }

  return (
    <div className="store-page">
      {/* Hero Banner */}
      <section className="store-hero">
        <div className="hero-content">
          <div className="store-badge">
            <FiShoppingBag /> TIENDA OFICIAL
          </div>
          <h1>Tienda <span>GRAPHYRA</span></h1>
          <p>Productos, cursos y recursos para expandir tu conocimiento</p>
          
          <div className="hero-search">
            <FiSearch className="search-icon" />
            <input 
              type="text"
              placeholder="Buscar productos..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>
        </div>
        
        {/* Floating products decoration */}
        <div className="floating-products">
          <div className="floating-item">📚</div>
          <div className="floating-item">👕</div>
          <div className="floating-item">🎓</div>
        </div>
      </section>

      {/* Featured Products Slider */}
      {featuredProducts.length > 0 && (
        <section className="featured-section">
          <div className="section-header">
            <h2>⭐ Productos Destacados</h2>
            <p>Lo más popular en nuestra tienda</p>
          </div>
          
          <div className="featured-slider">
            {featuredProducts.map(product => {
              const discount = getDiscountPercent(product);
              return (
                <Link
                  key={product.id}
                  to={`/store/${getProductSlug(product)}`}
                  className="featured-card"
                  onClick={(e) => openProductModal(product, e)}
                >
                  <div className="featured-image">
                    <img src={getProductImage(product)} alt={product.name} />
                    {discount > 0 && (
                      <span className="discount-badge">-{discount}%</span>
                    )}
                    <div className="featured-price">
                      {discount > 0 ? (
                        <>
                          <span className="original-price">${product.compare_price || product.price}</span>
                          <span className="sale-price">
                            ${product.price}
                          </span>
                        </>
                      ) : (
                        <span className="sale-price">${product.price}</span>
                      )}
                    </div>
                  </div>
                </Link>
              );
            })}
          </div>
        </section>
      )}

      {/* Main Store Content */}
      <section className="store-main">
        {/* Categories Sidebar */}
        <aside className="store-sidebar">
          <h3><FiFilter /> Categorías</h3>
          <ul className="category-list">
            <li>
              <button 
                className={selectedCategory === 'all' ? 'active' : ''}
                onClick={() => setSelectedCategory('all')}
              >
                <span className="cat-icon">🏪</span>
                <span>Todos</span>
                <span className="cat-count">{products.length}</span>
              </button>
            </li>
            {categories.filter(c => c.slug !== 'all').map(category => (
              <li key={category.id}>
                <button 
                  className={selectedCategory === category.slug ? 'active' : ''}
                  onClick={() => setSelectedCategory(category.slug)}
                >
                  <span className="cat-icon">{category.icon || '🏷️'}</span>
                  <span>{category.name}</span>
                  <span className="cat-count">
                    {category.product_count || products.filter(p => p.category_slug === category.slug || p.category_id === category.id).length}
                  </span>
                </button>
              </li>
            ))}
          </ul>
          
          <div className="sidebar-promo">
            <FiTag className="promo-icon" />
            <h4>Ofertas de Temporada</h4>
            {maxDiscountPercent > 0 ? (
              <p>Hasta {maxDiscountPercent}% de descuento en productos seleccionados</p>
            ) : (
              <p>Sin descuentos activos por ahora</p>
            )}
          </div>
        </aside>

        {/* Products Grid */}
        <div className="products-section">
          <div className="products-header">
            <span className="results-count">{filteredProducts.length} productos</span>
            <select className="sort-select">
              <option value="featured">Destacados</option>
              <option value="price-low">Precio: Menor a Mayor</option>
              <option value="price-high">Precio: Mayor a Menor</option>
              <option value="rating">Mejor Valorados</option>
            </select>
          </div>
          
          <div className="products-grid">
            {filteredProducts.map(product => {
              const discount = getDiscountPercent(product);
              return (
                <Link
                  key={product.id}
                  to={`/store/${getProductSlug(product)}`}
                  className="product-card"
                  onClick={(e) => openProductModal(product, e)}
                >
                  <div className="product-image">
                    <img src={getProductImage(product)} alt={product.name} />
                    <button className="wishlist-btn" onClick={(e) => e.preventDefault()}>
                      <FiHeart />
                    </button>
                    {discount > 0 && (
                      <span className="discount-tag">-{discount}%</span>
                    )}
                  </div>
                  <div className="product-info">
                    <span className="product-category">{product.category_name || product.category}</span>
                    <h3>{product.name}</h3>
                    <p>{product.short_description || product.description}</p>
                    {product.rating != null && (
                      <div className="product-rating">
                        <FiStar className="star-icon" />
                        <span>{product.rating}</span>
                      </div>
                    )}
                    <div className="product-price">
                      {discount > 0 ? (
                        <>
                          <span className="original">${product.compare_price || product.price}</span>
                          <span className="current">${product.price}</span>
                        </>
                      ) : (
                        <span className="current">${product.price}</span>
                      )}
                    </div>
                    <button className="add-to-cart" onClick={(e) => e.preventDefault()}>
                      <FiShoppingBag /> Añadir
                    </button>
                  </div>
                </Link>
              );
            })}
          </div>
          
          {filteredProducts.length === 0 && (
            <div className="no-products">
              <span className="empty-icon">🔍</span>
              <h3>No se encontraron productos</h3>
              <p>Intenta con otros términos de búsqueda o categoría</p>
            </div>
          )}
        </div>
      </section>

      {selectedProduct && (
        <ProductModal product={selectedProduct} onClose={closeProductModal} />
      )}

      {/* Benefits Banner */}
      <section className="store-benefits">
        <div className="benefit">
          <span className="benefit-icon">🚚</span>
          <h4>Envío Gratis</h4>
          <p>En pedidos mayores a $50</p>
        </div>
        <div className="benefit">
          <span className="benefit-icon">🔒</span>
          <h4>Pago Seguro</h4>
          <p>Transacciones encriptadas</p>
        </div>
        <div className="benefit">
          <span className="benefit-icon">↩️</span>
          <h4>Devoluciones</h4>
          <p>30 días para cambios</p>
        </div>
        <div className="benefit">
          <span className="benefit-icon">💬</span>
          <h4>Soporte 24/7</h4>
          <p>Estamos para ayudarte</p>
        </div>
      </section>
    </div>
  );
};

export default Store;
